import { useId } from 'react';

/*
 * Small decorative scenery shared by the redrawn screens: a leaf, a leafy
 * sprig, a lab building thumbnail, a salad bowl and the mountain-lake
 * landscape from the report covers. All aria-hidden.
 */

/* A soft green leaf; rotate/flip it with CSS. */
export function Leaf({ className }) {
  return (
    <svg className={className} viewBox="0 0 60 120" aria-hidden="true" focusable="false">
      <path d="M30 118C8 92 2 58 14 30 20 16 27 6 30 2c3 4 10 14 16 28 12 28 6 62-16 88Z" fill="currentColor" />
      <path d="M30 116V10" stroke="#fff" strokeOpacity="0.45" strokeWidth="2" />
      <path d="M30 40l-10-8M30 60l-13-10M30 80l-12-9M30 50l10-8M30 70l12-10M30 90l11-9" stroke="#fff" strokeOpacity="0.3" strokeWidth="1.6" />
    </svg>
  );
}

/* A small lab building, as on the mockups' search-result thumbnails. */
export function BuildingThumb({ className }) {
  return (
    <svg className={className} viewBox="0 0 80 56" aria-hidden="true" focusable="false">
      <rect width="80" height="56" fill="#dfeafc" />
      <rect y="40" width="80" height="16" fill="#cfe0f6" />
      <ellipse cx="12" cy="42" rx="10" ry="7" fill="#9fd0a8" />
      <ellipse cx="70" cy="43" rx="9" ry="6" fill="#9fd0a8" />
      <rect x="20" y="16" width="40" height="30" fill="#f6f9ff" />
      <rect x="20" y="13" width="40" height="5" fill="#2f63c9" />
      <rect x="34" y="8" width="12" height="8" fill="#f6f9ff" />
      <g fill="#7fa4e6">
        <rect x="24" y="21" width="6" height="5" />
        <rect x="33" y="21" width="6" height="5" />
        <rect x="42" y="21" width="6" height="5" />
        <rect x="51" y="21" width="6" height="5" />
        <rect x="24" y="30" width="6" height="5" />
        <rect x="33" y="30" width="6" height="5" />
        <rect x="42" y="30" width="6" height="5" />
        <rect x="51" y="30" width="6" height="5" />
      </g>
      <rect x="36" y="38" width="8" height="8" fill="#2f63c9" />
    </svg>
  );
}

/*
 * A detailed modern lab building for search-result thumbnails: blue sky with
 * a cloud, a white tower with ribbon windows beside a glass curtain wall,
 * a canopy entrance and trees. `variant` (0–2) shifts the massing so three
 * rows do not look identical.
 */
export function LabBuildingThumb({ className, variant = 0 }) {
  const uid = useId();
  const sky = `${uid}-sky`;
  const glass = `${uid}-glass`;
  const shade = `${uid}-shade`;
  const v = variant % 3;
  const towerX = [10, 44, 14][v];
  const glassX = [34, 12, 40][v];
  const glassH = [36, 30, 40][v];
  return (
    <svg className={className} viewBox="0 0 80 56" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={sky} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3d8fe8" />
          <stop offset="0.7" stopColor="#9ccaf5" />
          <stop offset="1" stopColor="#d6ebfb" />
        </linearGradient>
        <linearGradient id={glass} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#bfe0fb" />
          <stop offset="0.45" stopColor="#5e9fdc" />
          <stop offset="0.55" stopColor="#8cc0ef" />
          <stop offset="1" stopColor="#2f6fb8" />
        </linearGradient>
        <linearGradient id={shade} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#dfe6ef" />
        </linearGradient>
      </defs>
      <rect width="80" height="56" fill={`url(#${sky})`} />
      <g fill="#fff" opacity="0.85">
        <ellipse cx="62" cy="9" rx="9" ry="3.2" />
        <ellipse cx="67" cy="7" rx="5" ry="3" />
        <ellipse cx="14" cy="6" rx="6" ry="2" opacity="0.7" />
      </g>
      {/* glass curtain wall */}
      <rect x={glassX} y={50 - glassH} width="30" height={glassH} fill={`url(#${glass})`} />
      <g stroke="#e8f4ff" strokeOpacity="0.55" strokeWidth="0.6">
        {[6, 12, 18, 24].map((dx) => (
          <path key={dx} d={`M${glassX + dx} ${50 - glassH}V50`} />
        ))}
        {Array.from({ length: Math.floor(glassH / 5) }, (_, i) => (
          <path key={i} d={`M${glassX} ${50 - glassH + 5 + i * 5}h30`} />
        ))}
      </g>
      <path d={`M${glassX} ${50 - glassH}h30v3h-30Z`} fill="#eef3f9" />
      {/* white tower with ribbon windows */}
      <rect x={towerX} y="12" width="24" height="38" fill={`url(#${shade})`} />
      <rect x={towerX - 1} y="10" width="26" height="3" fill="#f4f7fb" />
      <g fill="#4a86c8">
        {[17, 23, 29, 35, 41].map((y) => (
          <rect key={y} x={towerX + 3} y={y} width="18" height="3.2" rx="0.6" />
        ))}
      </g>
      <g fill="#9cc6ee" opacity="0.7">
        {[17, 23, 29, 35, 41].map((y) => (
          <rect key={y} x={towerX + 3} y={y} width="6" height="1.4" />
        ))}
      </g>
      {/* canopy and door */}
      <rect x={towerX + 6} y="44" width="12" height="6" fill="#2b5c9e" />
      <rect x={towerX + 3} y="42.5" width="18" height="2" fill="#e7edf5" />
      {/* ground and trees */}
      <rect y="50" width="80" height="6" fill="#d9e3ea" />
      <rect y="49" width="80" height="1.4" fill="#b8c8d6" />
      <g>
        <circle cx="5" cy="45" r="5" fill="#4f9a55" />
        <circle cx="9" cy="47" r="4" fill="#67b26a" />
        <circle cx="74" cy="45" r="5.5" fill="#4a9250" />
        <circle cx="70" cy="48" r="3.6" fill="#6db870" />
      </g>
    </svg>
  );
}

/* A salad bowl for "Your Personalised Plan". */
export function FoodBowl({ className }) {
  return (
    <svg className={className} viewBox="0 0 80 64" aria-hidden="true" focusable="false">
      <rect width="80" height="64" rx="10" fill="#f3efe8" />
      <ellipse cx="40" cy="34" rx="28" ry="22" fill="#fff" />
      <ellipse cx="40" cy="33" rx="23" ry="17" fill="#5f9d43" />
      <circle cx="31" cy="28" r="6" fill="#e2532f" />
      <circle cx="47" cy="38" r="5.5" fill="#e2532f" />
      <ellipse cx="46" cy="25" rx="7" ry="4" fill="#f2c14e" />
      <ellipse cx="32" cy="40" rx="6" ry="3.5" fill="#d9a55b" />
      <circle cx="39" cy="33" r="3" fill="#8bc34a" />
      <path d="M24 34c4-2 8 2 12 0M44 30c3 2 6 0 9 2" stroke="#3f7a2a" strokeWidth="2" fill="none" strokeLinecap="round" />
    </svg>
  );
}

/* A sprig of glossy leaves on a curved stem, growing up from the bottom. */
/*
 * `tone` is each leaf's shade relative to a mid green, as a color-mix() mix-in.
 * A page that recolours the sprig (Brand equity's cover) sets the leaves'
 * fill to color-mix(in srgb, <its colour>, var(--leaf-tone)), keeping the
 * same highlights and shadows in the new hue. Unstyled, the greens below draw.
 */
const SPRIG_LEAVES = [
  { x: 54, y: 206, r: -150, s: 1.15, c: '#2f7d3f', tone: 'black 26%' },
  { x: 50, y: 178, r: -28, s: 1.1, c: '#4c9a45', tone: 'black 4%' },
  { x: 47, y: 146, r: -142, s: 1.05, c: '#3b8a43', tone: 'black 16%' },
  { x: 46, y: 116, r: -36, s: 1, c: '#5aa64c', tone: 'white 8%' },
  { x: 47, y: 86, r: -136, s: 0.9, c: '#3f8f45', tone: 'black 12%' },
  { x: 51, y: 60, r: -44, s: 0.8, c: '#67b257', tone: 'white 18%' },
  { x: 56, y: 38, r: -120, s: 0.66, c: '#4f9d4a', tone: 'white 2%' },
  { x: 60, y: 24, r: -70, s: 0.55, c: '#7cc06a', tone: 'white 32%' },
];

export function LeafSprig({ className }) {
  return (
    <svg className={className} viewBox="-10 0 130 240" aria-hidden="true" focusable="false">
      <path
        className="leaf-sprig__stem"
        d="M58 240C50 200 44 150 46 110 47 80 52 50 60 24"
        fill="none"
        stroke="#3d7f3a"
        strokeWidth="2.4"
      />
      {SPRIG_LEAVES.map((l) => (
        <g key={`${l.x}-${l.y}`} transform={`translate(${l.x} ${l.y}) rotate(${l.r}) scale(${l.s})`}>
          <path
            className="leaf-sprig__leaf"
            d="M0 0C10-13 34-17 54-4 36 11 12 12 0 0Z"
            fill={l.c}
            style={{ '--leaf-tone': l.tone }}
          />
          <path d="M2 0C16-4 34-5 52-4" stroke="#fff" strokeOpacity="0.35" strokeWidth="1.2" fill="none" />
        </g>
      ))}
    </svg>
  );
}

/*
 * The cover's landscape: a warm sky with the sun low on the right, snowy
 * mountains, pine woods and a still lake with reflections. Fills its box
 * (preserveAspectRatio slice), so clip or crop it with CSS.
 */
export function MountainLake({ className }) {
  const uid = useId();
  const sky = `${uid}-sky`;
  const sun = `${uid}-sun`;
  const lake = `${uid}-lake`;
  const trees = (y, h, n, from, step, fill) =>
    Array.from({ length: n }, (_, i) => {
      const x = from + i * step;
      const th = h * (0.7 + ((i * 37) % 10) / 22);
      return <path key={`${y}-${i}`} d={`M${x} ${y - th}l${th * 0.28} ${th}h${-th * 0.56}Z`} fill={fill} />;
    });
  return (
    <svg className={className} viewBox="0 0 200 320" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={sky} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dcebf7" />
          <stop offset="0.45" stopColor="#eef4f3" />
          <stop offset="0.62" stopColor="#fbefd2" />
        </linearGradient>
        <radialGradient id={sun} cx="0.82" cy="0.5" r="0.45">
          <stop offset="0" stopColor="#fffbea" />
          <stop offset="0.25" stopColor="#ffe9b0" stopOpacity="0.9" />
          <stop offset="1" stopColor="#ffe9b0" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={lake} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#c9dfe6" />
          <stop offset="0.5" stopColor="#8fb3c4" />
          <stop offset="1" stopColor="#5f8ea3" />
        </linearGradient>
      </defs>
      <rect width="200" height="320" fill={`url(#${sky})`} />
      <rect width="200" height="320" fill={`url(#${sun})`} />
      {/* far range */}
      <path d="M0 176l26-40 18 18 30-58 22 34 18-22 36 52 22-30 28 46V200H0Z" fill="#a7bccd" />
      <path d="M74 96l-12 23 8-4 6 6 6-9 6 6Zm48 28l-8 11 6-2 4 4 5-6Zm-96 12l-8 12 6-3 3 3Z" fill="#fff" opacity="0.9" />
      {/* near range */}
      <path d="M0 196l32-30 24 20 34-38 30 34 26-20 54 40V214H0Z" fill="#7d98ad" />
      <path d="M90 148l-10 12 6-2 4 5 5-6 5 3Z" fill="#eef4f8" opacity="0.85" />
      {/* far woods */}
      <rect y="196" width="200" height="20" fill="#3e6a4c" />
      <g>{trees(206, 22, 26, 2, 8, '#335d41')}</g>
      {/* lake with reflections */}
      <rect y="214" width="200" height="74" fill={`url(#${lake})`} />
      <path d="M0 214l32 22 24-14 34 30 30-24 26 16 54-30Z" fill="#6f8ea2" opacity="0.28" />
      <path d="M150 220h40M140 232h50M156 246h30M20 240h40M30 258h34" stroke="#fff" strokeOpacity="0.5" strokeWidth="1.2" />
      {/* near woods and shore */}
      <path d="M0 280c40-8 90-6 200 4V320H0Z" fill="#2c4f37" />
      <g>{trees(300, 46, 9, 4, 24, '#24452f')}</g>
    </svg>
  );
}
